import Swiper, { Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

Swiper.use([Navigation, Pagination]);


function isValidSelector(selector) {
    if (!selector || typeof selector !== 'string') return false;
    const trimmed = selector.trim();
    if (trimmed === '' || trimmed === '#' || trimmed === '.') return false;
    try {
        document.querySelector(trimmed);
        return true;
    } catch {
        return false;
    }
}

function updateSwiperParams(swiper, options) {
    if (!swiper) return;

    const { navigation, pagination, ...rest } = options;
    Object.assign(swiper.params, rest);
    if (swiper.originalParams) {
        Object.assign(swiper.originalParams, rest);
    }

    swiper.currentBreakpoint = undefined;
    if (typeof swiper.setBreakpoint === 'function') {
        swiper.setBreakpoint();
    }

    if (navigation) {
        if (typeof swiper.params.navigation === 'object' && swiper.params.navigation !== null) {
            Object.assign(swiper.params.navigation, navigation);
        } else {
            swiper.params.navigation = {
                disabledClass: 'swiper-button-disabled',
                hiddenClass: 'swiper-button-hidden',
                lockClass: 'swiper-button-lock',
                navigationDisabledClass: 'swiper-navigation-disabled',
                ...navigation,
            };
        }

        if (swiper.navigation) {
            swiper.navigation.destroy();
            swiper.navigation.init();
            swiper.navigation.update();
        }
    }

    if (pagination) {
        if (typeof swiper.params.pagination === 'object' && swiper.params.pagination !== null) {
            Object.assign(swiper.params.pagination, pagination);
        } else {
            swiper.params.pagination = { ...pagination };
        }
    }

    swiper.update();
}

function initReviewsSwiper(selector = '.reviews-swiper') {
    if (!isValidSelector(selector)) return;

    document.querySelectorAll(selector).forEach(el => {
        if (!el) return;

        const nextEl = el.querySelector(".swiper-button-next");
        const prevEl = el.querySelector(".swiper-button-prev");
        const paginationEl = el.querySelector(".swiper-pagination");

        const options = {
            modules: [Navigation, Pagination],
            spaceBetween: 24,

            pagination: {
                el: paginationEl,
                clickable: true,
            },
            navigation: nextEl && prevEl ? {
                nextEl,
                prevEl,
                disabledClass: 'swiper-button-disabled',
                hiddenClass: 'swiper-button-hidden',
                lockClass: 'swiper-button-lock',
                navigationDisabledClass: 'swiper-navigation-disabled',
            } : false,
            breakpoints: {
                320: {
                    slidesPerView: 1.2,
                    spaceBetween: 16,
                },
                640: {
                    slidesPerView: 1.5,
                    spaceBetween: 16,
                },
                1024: {
                    slidesPerView: 2.5,
                    spaceBetween: 20,
                },
                1280: {
                    slidesPerView: 3.5,
                    spaceBetween: 24,
                },
                1920: {
                    slidesPerView: 3.5,
                    spaceBetween: 24,
                }
            },
        };

        if (el.swiper) {
            el.swiper.destroy(true, true);
        }

        new Swiper(el, options);
    });
}

function getProductSwiperOptions(nextEl, prevEl, paginationEl) {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    return {
        modules: [Navigation, Pagination],
        slidesPerView: 1.45,
        centeredSlides: isMobile,
        initialSlide: isMobile ? 1 : 0,
        spaceBetween: 14,
        watchOverflow: true,
        rewind: true,
        pagination: paginationEl ? {
            el: paginationEl,
            clickable: true,
        } : false,
        navigation: nextEl && prevEl ? {
            nextEl,
            prevEl,
            disabledClass: 'swiper-button-disabled',
            hiddenClass: 'swiper-button-hidden',
            lockClass: 'swiper-button-lock',
            navigationDisabledClass: 'swiper-navigation-disabled',
        } : false,
        breakpoints: {
            320: {
                slidesPerView: 1.45,
                spaceBetween: 14,
                centeredSlides: true,
            },
            480: {
                slidesPerView: 1.65,
                spaceBetween: 16,
                centeredSlides: true,
            },
            640: {
                slidesPerView: 2.5,
                spaceBetween: 16,
                centeredSlides: false,
            },
            768: {
                slidesPerView: 3.2,
                spaceBetween: 16,
                centeredSlides: false,
            },
            1024: {
                slidesPerView: 4.2,
                spaceBetween: 20,
                centeredSlides: false,
            },
            1280: {
                slidesPerView: 5.2,
                spaceBetween: 20,
                centeredSlides: false,
            },
        },
    };
}

function bindSwiperNavigation(prevEl, nextEl, swiperInstance) {
    if (prevEl) {
        prevEl.onclick = (e) => {
            e.preventDefault();
            if (swiperInstance) {
                if (swiperInstance.isBeginning && swiperInstance.params.rewind) {
                    swiperInstance.slideTo(swiperInstance.slides.length - 1);
                } else {
                    swiperInstance.slidePrev();
                }
            }
        };
    }

    if (nextEl) {
        nextEl.onclick = (e) => {
            e.preventDefault();
            if (swiperInstance) {
                if (swiperInstance.isEnd && swiperInstance.params.rewind) {
                    swiperInstance.slideTo(0);
                } else {
                    swiperInstance.slideNext();
                }
            }
        };
    }
}

function initAllSwipers(target) {
    let elements = [];

    if (typeof target === 'string') {
        if (isValidSelector(target)) {
            elements = Array.from(document.querySelectorAll(target));
        }
    } else if (target && (target instanceof Element || target.nodeType === 1)) {
        if (target.classList.contains('swiper')) {
            elements = [target];
        } else {
            elements = Array.from(target.querySelectorAll('.swiper'));
        }
    }

    elements.forEach(el => {
        if (!el) return;

        const parent = el.parentElement;
        const nextEl = el.querySelector(".swiper-button-next") || parent?.querySelector(".swiper-button-next");
        const prevEl = el.querySelector(".swiper-button-prev") || parent?.querySelector(".swiper-button-prev");
        const paginationEl = el.querySelector(".swiper-pagination") || parent?.querySelector(".swiper-pagination");

        const options = getProductSwiperOptions(nextEl, prevEl, paginationEl);

        if (el.swiper) {
            el.swiper.destroy(true, true);
        }
        const swiperInstance = new Swiper(el, options);

        bindSwiperNavigation(prevEl, nextEl, swiperInstance);
    });
}
function initNewProductsSwiper(root = document) {
    const el = root.querySelector(".new-products-swiper");
    if (!el) return;

    const parent = el.parentElement;
    const nextEl = el.querySelector(".swiper-button-next") || parent?.querySelector(".swiper-button-next");
    const prevEl = el.querySelector(".swiper-button-prev") || parent?.querySelector(".swiper-button-prev");
    const paginationEl = el.querySelector(".swiper-pagination") || parent?.querySelector(".swiper-pagination");

    const options = getProductSwiperOptions(nextEl, prevEl, paginationEl);

    if (el.swiper) {
        el.swiper.destroy(true, true);
    }
    const swiperInstance = new Swiper(el, options);

    bindSwiperNavigation(prevEl, nextEl, swiperInstance);
}

// wystaw globalnie
window.initNewProductsSwiper = initNewProductsSwiper;
window.initReviewsSwiper = initReviewsSwiper;

function initPromotionsSwiper(root = document) {
    const el = root.querySelector(".promotions-swiper");
    if (!el) return;

    const parent = el.parentElement;
    const nextEl = el.querySelector(".swiper-button-next") || parent?.querySelector(".swiper-button-next");
    const prevEl = el.querySelector(".swiper-button-prev") || parent?.querySelector(".swiper-button-prev");
    const paginationEl = el.querySelector(".swiper-pagination") || parent?.querySelector(".swiper-pagination");

    const options = getProductSwiperOptions(nextEl, prevEl, paginationEl);

    if (el.swiper) {
        el.swiper.destroy(true, true);
    }
    const swiperInstance = new Swiper(el, options);

    bindSwiperNavigation(prevEl, nextEl, swiperInstance);
}

window.initPromotionsSwiper = initPromotionsSwiper;

function initBrandsSwiper(root = document) {
    const elements = root.querySelectorAll(".brands-swiper");
    elements.forEach((el) => {
        const parent = el.closest(".brands-grid-root") || el.parentElement;
        if (parent) {
            const shimmer = parent.querySelector(".brands-shimmer, #brands-shimmer");
            if (shimmer) {
                shimmer.remove();
            }
        }
        el.classList.remove("!hidden", "hidden");
        el.style.display = "block";

        const nextEl = el.querySelector(".swiper-button-next") || parent?.querySelector(".swiper-button-next");
        const prevEl = el.querySelector(".swiper-button-prev") || parent?.querySelector(".swiper-button-prev");
        const paginationEl = el.querySelector(".swiper-pagination") || parent?.querySelector(".swiper-pagination");

        let isBanner = false;
        try {
            isBanner = el.querySelector('[class*="610"]') !== null || Boolean(el.querySelector('a[class*="aspect-"]'));
        } catch (e) {
            isBanner = false;
        }

        const options = {
            modules: [Navigation, Pagination],
            slidesPerView: isBanner ? 2 : 6,
            initialSlide: 0,
            spaceBetween: 20,
            rewind: true,
            grabCursor: true,
            allowTouchMove: true,
            watchOverflow: false,
            observer: true,
            observeParents: true,
            resizeObserver: true,
            pagination: {
                el: paginationEl,
                clickable: true,
            },
            navigation: nextEl && prevEl ? {
                nextEl,
                prevEl,
                disabledClass: 'swiper-button-disabled',
                hiddenClass: 'swiper-button-hidden',
                lockClass: 'swiper-button-lock',
                navigationDisabledClass: 'swiper-navigation-disabled',
            } : false,
            breakpoints: isBanner ? {
                320: {
                    slidesPerView: 1.2,
                    spaceBetween: 16,
                },
                640: {
                    slidesPerView: 2,
                    spaceBetween: 16,
                },
                1024: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                },
                1280: {
                    slidesPerView: 2,
                    spaceBetween: 24,
                },
            } : {
                320: {
                    slidesPerView: 2.5,
                    spaceBetween: 10,
                },
                640: {
                    slidesPerView: 3.2,
                    spaceBetween: 16,
                },
                1024: {
                    slidesPerView: 4.5,
                    spaceBetween: 20,
                },
                1280: {
                    slidesPerView: 6,
                    spaceBetween: 24,
                },
            },
        };

        let swiperInstance = el.swiper;
        if (swiperInstance) {
            updateSwiperParams(swiperInstance, options);
        } else {
            swiperInstance = new Swiper(el, options);
        }

        if (prevEl) {
            prevEl.onclick = (e) => {
                e.preventDefault();
                if (swiperInstance) {
                    if (swiperInstance.isBeginning && swiperInstance.params.rewind) {
                        swiperInstance.slideTo(swiperInstance.slides.length - 1);
                    } else {
                        swiperInstance.slidePrev();
                    }
                }
            };
        }

        if (nextEl) {
            nextEl.onclick = (e) => {
                e.preventDefault();
                if (swiperInstance) {
                    if (swiperInstance.isEnd && swiperInstance.params.rewind) {
                        swiperInstance.slideTo(0);
                    } else {
                        swiperInstance.slideNext();
                    }
                }
            };
        }
    });
}

window.Swiper = Swiper;
window.initBrandsSwiper = initBrandsSwiper;



let featuredSwiper = null;

function initFeaturedProductsSwiper() {
    const el = document.querySelector(".featured-mobile-swiper");
    if (!el) return;

    const isMobile = window.innerWidth < 1180;

    if (isMobile && !featuredSwiper) {
        const paginationEl = el.querySelector(".swiper-pagination");

        featuredSwiper =
            new Swiper(el, {
                modules: [Pagination],

                slidesPerView: "auto",
                centeredSlides: true,
                spaceBetween: 16,
                initialSlide: 1,
                watchOverflow: true,

                pagination: {
                    el: paginationEl,
                    clickable: true,
                },
            });
    }

    if (!isMobile && featuredSwiper) {
        featuredSwiper.destroy(true, true);
        featuredSwiper = null;
    }
}

window.addEventListener("load", initFeaturedProductsSwiper);
window.addEventListener("resize", initFeaturedProductsSwiper);

export {
    initReviewsSwiper,
    initAllSwipers,
    initNewProductsSwiper,
    initPromotionsSwiper,
    initBrandsSwiper,
    initFeaturedProductsSwiper,
};

window.initReviewsSwiper = initReviewsSwiper;
window.initAllSwipers = initAllSwipers;
window.initNewProductsSwiper = initNewProductsSwiper;
window.initPromotionsSwiper = initPromotionsSwiper;
window.initBrandsSwiper = initBrandsSwiper;
window.initFeaturedProductsSwiper = initFeaturedProductsSwiper;
